<template>
  <div class="dashboard-fleet-inventory">
    <div class="fi-header">
      <h3 class="fi-title">{{ $t('org-dashboard.fleet-inventory-title') }}</h3>
      <span v-if="outdatedTotal > 0" class="fi-outdated-badge fi-outdated-badge--clickable" @click="$emit('firmware-drilldown', { status: 'outdated' })">
        <v-icon x-small color="#d97706" class="mr-1">mdi-alert</v-icon>
        {{ outdatedTotal }} {{ $t('org-dashboard.firmware-outdated') }}
      </span>
    </div>

    <!-- Firmware grid by model -->
    <div v-if="fwByModel.length" class="fi-fw-section">
      <div class="fi-fw-header">
        <v-icon size="14" color="#6366F1" class="mr-1">mdi-chip</v-icon>
        <span class="fi-fw-title">{{ $t('org-dashboard.firmware-title') }}</span>
      </div>
      <div class="fi-fw-grid">
        <div
          v-for="(model, mi) in fwByModel"
          :key="mi"
          class="fi-fw-card"
          :class="{ 'fi-fw-card--outdated': model.outdatedCount > 0 }"
        >
          <div class="fi-fw-card-top">
            <span class="fi-fw-vendor">{{ model.vendor }}</span>
            <v-icon
              v-if="model.outdatedCount === 0"
              size="14"
              color="#22c55e"
            >mdi-check-circle</v-icon>
            <span v-else class="fi-fw-warn-count fi-fw-warn-count--clickable" @click.stop="$emit('firmware-drilldown', { vendor: model.vendor, model: model.model, status: 'outdated', versions: model.firmwareVersions })">{{ model.outdatedCount }}</span>
          </div>
          <span class="fi-fw-model">{{ model.model }}</span>
          <div class="fi-fw-card-meta">
            <span class="fi-fw-total">{{ model.totalCps }} {{ $t('org-dashboard.firmware-units') }}</span>
          </div>
          <div class="fi-fw-versions">
            <div
              v-for="(v, vi) in (model.firmwareVersions || [])"
              :key="vi"
              class="fi-fw-ver fi-fw-ver--clickable"
              @click="$emit('firmware-drilldown', { vendor: model.vendor, model: model.model, version: v.version, versions: model.firmwareVersions })"
            >
              <span class="fi-fw-ver-bar" :class="v.isLatest ? 'fi-fw-ver-bar--ok' : 'fi-fw-ver-bar--warn'"></span>
              <span class="fi-fw-ver-name" :title="v.version">{{ v.version }}</span>
              <span class="fi-fw-ver-count">{{ v.count }}</span>
              <span v-if="v.isLatest" class="fi-fw-chip fi-fw-chip--ok">{{ $t('org-dashboard.firmware-latest') }}</span>
              <span v-else class="fi-fw-chip fi-fw-chip--warn">{{ $t('org-dashboard.firmware-update-needed') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!fwByModel.length" class="fi-empty">
      <v-icon color="#d1d5db" size="32">mdi-server-network-off</v-icon>
      <span>{{ $t('org-dashboard.health-no-data') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardFleetInventory',

  props: {
    firmware: { type: Object, default: null },
  },

  computed: {
    fwByModel() {
      return (this.firmware && this.firmware.byModel) || []
    },
    outdatedTotal() {
      let count = 0
      this.fwByModel.forEach(m => {
        (m.firmwareVersions || []).forEach(v => {
          if (!v.isLatest) count += v.count
        })
      })
      return count
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-fleet-inventory {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.fi-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.fi-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.fi-outdated-badge {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #d97706;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 3px 8px;

  &--clickable {
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: #fef3c7;
      border-color: #f59e0b;
    }
  }
}

/* ── Firmware grid ── */
.fi-fw-section {
  // no extra margin needed
}

.fi-fw-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.fi-fw-title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.fi-fw-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1264px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.fi-fw-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  transition: border-color 0.2s;

  &--outdated {
    border-color: #fde68a;
    background: #fffdf7;
  }
}

.fi-fw-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.fi-fw-vendor {
  font-size: 10px;
  font-weight: 600;
  color: #6366f1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fi-fw-warn-count {
  font-size: 10px;
  font-weight: 700;
  color: white;
  background: #f59e0b;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &--clickable {
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.25);
    }
  }
}

.fi-fw-model {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  display: block;
  margin-bottom: 6px;
  line-height: 1.3;
}

.fi-fw-card-meta {
  margin-bottom: 8px;
}

.fi-fw-total {
  font-size: 10px;
  color: #9ca3af;
}

.fi-fw-versions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fi-fw-ver {
  display: flex;
  align-items: center;
  gap: 6px;

  &--clickable {
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 4px;
    margin: -2px -4px;
    transition: background 0.15s;

    &:hover {
      background: #f0f9ff;
    }
  }
}

.fi-fw-ver-bar {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  flex-shrink: 0;

  &--ok { background: #22c55e; }
  &--warn { background: #f59e0b; }
}

.fi-fw-ver-name {
  font-size: 10px;
  font-family: monospace;
  color: #374151;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fi-fw-ver-count {
  font-size: 11px;
  font-weight: 700;
  color: #1f2937;
  flex-shrink: 0;
}

.fi-fw-chip {
  font-size: 8px;
  font-weight: 600;
  border-radius: 3px;
  padding: 1px 4px;
  white-space: nowrap;
  flex-shrink: 0;

  &--ok {
    color: #059669;
    background: #ecfdf5;
  }

  &--warn {
    color: #d97706;
    background: #fffbeb;
  }
}

.fi-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 0;

  span {
    font-size: 12px;
    color: #9ca3af;
  }
}
</style>
